/**
 * Items Table — Mobile cards + Desktop table + per-item decide buttons.
 * Inputs:
 *   pr                  full PR object (uses .items, .currency, .exch_rate, .supplier)
 *   acting              boolean — disables the Auto-decide button while a request is in flight
 *   onItemDecide(it,a)  handler — a ∈ { 'approve' | 'hold' | 'reject' | 'clear' }
 *   onAutoDecide()      handler — auto-approve all green items
 *   onFinalize()        handler — roll up per-item decisions to a PR-level decision
 */
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import Sparkline from "@/components/Sparkline";
import ItemInsightPopover from "@/components/ItemInsightPopover";
import { flagClass, priceLabel, vendorLabel, fmtAmt, fmtDate, freshness } from "@/lib/flags";
import { CheckCircle2, XCircle, PauseCircle, ChevronRight, RotateCcw, Wand2, Zap, Lock, TrendingDown } from "lucide-react";

/**
 * "Last bought" chip — small age-coded indicator showing when we most recently
 * paid for this item. Uses `it.last_vdate` (this supplier's own last GPurci
 * bill_date, populated by fetch_supplier_last_rates), falling back to
 * `it.last_paid_vdate` (cross-vendor most recent, again from GPurci.bill_date).
 * The dot colour is driven by `freshness()`: emerald ≤30d, amber ≤180d,
 * slate >180d. For jobwork items we skip the date pill — the "approved ₹X"
 * copy already conveys the rate is a master-approved reference, not a purchase.
 */
function LastBoughtChip({ it }) {
  if (it.jobtype) return null;
  const vdate = it.last_vdate || it.last_paid_vdate;
  if (!vdate) return null;
  const fr = freshness(vdate);
  if (!fr) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] ${fr.textClass}`}
      title={`Last purchased ${fr.ago}`}
      data-testid={`item-last-bought-${it.icode}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${fr.dotClass}`} />
      {fmtDate(vdate)}
    </span>
  );
}

function itemActionPill(d) {
  if (!d?.action) return null;
  const isL1 = d.level === "L1";
  let cls = "bg-slate-50 text-slate-700 border-slate-200";
  if (d.action === "APPROVED") {
    cls = isL1
      ? "bg-emerald-100 text-emerald-900 border-emerald-400"
      : "bg-emerald-50 text-emerald-800 border-emerald-200";
  } else if (d.action === "REJECTED") {
    cls = isL1
      ? "bg-rose-100 text-rose-900 border-rose-400 ring-1 ring-rose-300"
      : "bg-rose-50 text-rose-900 border-rose-200";
  } else if (d.action === "HOLD") {
    cls = isL1
      ? "bg-yellow-100 text-yellow-900 border-yellow-400"
      : "bg-yellow-50 text-yellow-900 border-yellow-200";
  }
  const label = isL1 ? `L1 ${d.action}` : d.action;
  const title = isL1 && d.by ? `${label} by ${d.by} — immutable for L2` : label;
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold border ${cls}`}
      title={title}
    >
      {isL1 && d.action === "REJECTED" && <Lock size={9} className="inline" />}
      {label}
    </span>
  );
}

function decideBtnClass(isActive, palette) {
  // palette: 'emerald' | 'yellow' | 'rose'
  if (isActive) {
    if (palette === "emerald") return "bg-emerald-600 text-white border-emerald-700";
    if (palette === "yellow") return "bg-yellow-500 text-white border-yellow-600";
    if (palette === "rose") return "bg-rose-600 text-white border-rose-700";
  }
  if (palette === "emerald") return "bg-white hover:bg-emerald-50 text-emerald-700 border-emerald-200";
  if (palette === "yellow") return "bg-white hover:bg-yellow-50 text-yellow-700 border-yellow-200";
  if (palette === "rose") return "bg-white hover:bg-rose-50 text-rose-700 border-rose-200";
  return "";
}

function VendorTooltipContent({ vf }) {
  if (!vf.lowest_vendor || !vf.lowest_vdate) {
    return <div className="text-xs">No approved purchase in last 12 mo.</div>;
  }
  return (
    <div className="space-y-1 text-xs">
      <div className="font-semibold">Cheapest vendor (last 12 mo)</div>
      <div>{vf.lowest_vendor}</div>
      <div className="text-slate-300">
        Last bought on <b>{fmtDate(vf.lowest_vdate)}</b> @ ₹{fmtAmt(vf.lowest_rate)}
      </div>
    </div>
  );
}

function ItemDecideButtons({ it, onDecide, sizeMode = "desktop", locked = false, lockedBy = null }) {
  const isDesktop = sizeMode === "desktop";
  const iconSz = isDesktop ? 14 : 12;
  const btnCls = isDesktop ? "p-1 rounded transition-colors border" : "px-2 py-1 rounded text-[11px] font-semibold border";
  const tidSuffix = isDesktop ? "" : "-mobile";
  if (locked) {
    // L2 view of an L1-rejected line — buttons swapped for a lock chip so
    // the approver knows they cannot overturn this line here.
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={`item-locked-by-l1${tidSuffix}-${it.icode}`}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold border bg-rose-50 text-rose-800 border-rose-300 cursor-not-allowed"
          >
            <Lock size={iconSz - 2} /> Locked by L1
          </span>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-[240px]">
          <div className="text-xs">
            <div className="font-semibold mb-0.5">L1 rejected this line</div>
            {lockedBy && <div className="text-slate-300">by {lockedBy}</div>}
            <div className="text-slate-300 mt-1">
              L2 cannot overturn an L1 rejection. Ask the L1 Authoriser to clear it if this was in error.
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }
  return (
    <>
      <button
        type="button" title="Approve item"
        data-testid={`item-approve${tidSuffix}-${it.icode}`}
        onClick={() => onDecide(it, "approve")}
        className={`${btnCls} ${decideBtnClass(it.decision?.action === "APPROVED", "emerald")}`}
      >
        <CheckCircle2 size={iconSz} className={isDesktop ? "" : "inline mr-1"} />
        {!isDesktop && "Approve"}
      </button>
      <button
        type="button" title="Hold item"
        data-testid={`item-hold${tidSuffix}-${it.icode}`}
        onClick={() => onDecide(it, "hold")}
        className={`${btnCls} ${decideBtnClass(it.decision?.action === "HOLD", "yellow")}`}
      >
        <PauseCircle size={iconSz} className={isDesktop ? "" : "inline mr-1"} />
        {!isDesktop && "Hold"}
      </button>
      <button
        type="button" title="Reject item"
        data-testid={`item-reject${tidSuffix}-${it.icode}`}
        onClick={() => onDecide(it, "reject")}
        className={`${btnCls} ${decideBtnClass(it.decision?.action === "REJECTED", "rose")}`}
      >
        <XCircle size={iconSz} className={isDesktop ? "" : "inline mr-1"} />
        {!isDesktop && "Reject"}
      </button>
      {it.decision?.action && (
        <button
          type="button" title="Clear decision"
          data-testid={`item-clear${tidSuffix}-${it.icode}`}
          onClick={() => onDecide(it, "clear")}
          className={isDesktop
            ? "p-1 rounded bg-white hover:bg-slate-100 text-slate-500 border border-slate-200"
            : "px-1.5 py-1 rounded bg-white hover:bg-slate-100 text-slate-500 border border-slate-200"}
        >
          <RotateCcw size={isDesktop ? 12 : 11} />
        </button>
      )}
    </>
  );
}

function ItemCardMobile({ it, pr, onItemDecide, canOverrideL1 = false }) {
  const isForeign = pr.currency && pr.currency !== "INR";
  // L1 rejection lock: L2 (and non-Admin viewers) cannot overturn a line that
  // L1 has explicitly rejected. Admins & the original L1 (canOverrideL1=true)
  // keep the buttons enabled so they can correct mistakes.
  const l1Reject = it.decision?.level === "L1" && it.decision?.action === "REJECTED";
  const locked = l1Reject && !canOverrideL1;
  return (
    <div className="p-3 space-y-2" data-testid={`item-card-${it.icode}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-800 text-sm flex items-center gap-1.5 flex-wrap">
            <span className="min-w-0 truncate">{it.iname}</span>
            {it.jobtype && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[9px] font-bold bg-violet-100 text-violet-800 border border-violet-200 leading-none uppercase tracking-wide"
                data-testid={`item-jobtype-${it.icode}`}
                title={`Jobwork process — historical comparison filtered by this process`}
              >
                {it.jobtype}
              </span>
            )}
            <ItemInsightPopover item={it} supplierName={pr.supplier} />
          </div>
          <div className="text-[10px] text-slate-500 mono">{it.icode}</div>
          {/* Creator's per-line-item remark (PoRequesti.Remark) */}
          {it.remark && (
            <div
              className="mt-1 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 italic"
              data-testid={`item-remark-mobile-${it.icode}`}
              title={it.remark}
            >
              <span className="not-italic font-bold mr-1">Note:</span>{it.remark}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold num">
            {isForeign ? (
              <>
                <span className="text-[10px] text-slate-500 font-normal mr-0.5">{pr.currency}</span>
                {fmtAmt(it.rate)} × {it.qty}
              </>
            ) : (
              <>{fmtAmt(it.rate)} × {it.qty}</>
            )}
          </div>
          {it.amt != null && (
            <div
              className="text-xs font-semibold text-cyan-800 num"
              title={isForeign ? `Converted at ${pr.currency} ${fmtAmt(pr.exch_rate)} → ₹` : ""}
            >
              = ₹{fmtAmt(it.amt)}
            </div>
          )}
          {it.last_rate ? (
            <div className="text-[10px] text-slate-400 flex items-center gap-1.5 flex-wrap">
              <span>{it.jobtype ? "approved" : "was"} ₹{fmtAmt(it.last_rate)}</span>
              <LastBoughtChip it={it} />
            </div>
          ) : it.jobtype ? (
            <div className="text-[10px] text-slate-400 italic">no history for {it.jobtype}</div>
          ) : null}
          {it.jobtype && it.last_rate && it.price_flag && it.price_flag.score === 0 && (
            <div
              className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 leading-none"
              data-testid={`item-approved-rate-mobile-${it.icode}`}
              title={`Rate matches master-approved ₹${fmtAmt(it.last_rate)} for ${it.jobtype}`}
            >
              ✓ Matches approved
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <Sparkline points={it.price_history || []} />
        {itemActionPill(it.decision)}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${flagClass(it.price_flag.flag)}`}>
          Price · {priceLabel(it.price_flag)}
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border cursor-help ${flagClass(it.vendor_flag.flag)}`}>
              Vendor · {vendorLabel(it.vendor_flag)}
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[260px]">
            <VendorTooltipContent vf={it.vendor_flag} />
          </TooltipContent>
        </Tooltip>
      </div>
      {it.vendor_flag.lowest_vendor && it.vendor_flag.flag !== "green" && (
        <div className="text-[11px] text-slate-600 bg-slate-50 rounded px-2 py-1 flex items-start gap-1">
          <ChevronRight size={11} className="text-slate-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="truncate">
              {it.vendor_flag.same_vendor ? "Cheaper elsewhere: " : "Cheaper: "}
              <b>{it.vendor_flag.lowest_vendor}</b> @ ₹{fmtAmt(it.vendor_flag.lowest_rate)}
            </div>
            {it.vendor_flag.lowest_vdate && (
              <div className="text-[10px] text-slate-500">Last bought {fmtDate(it.vendor_flag.lowest_vdate)}</div>
            )}
          </div>
        </div>
      )}
      <div className="flex items-center justify-end gap-1.5 pt-1">
        {onItemDecide && (
          <ItemDecideButtons
            it={it}
            onDecide={onItemDecide}
            sizeMode="mobile"
            locked={locked}
            lockedBy={it.decision?.by}
          />
        )}
      </div>
    </div>
  );
}

function ItemRowDesktop({ it, pr, onItemDecide, canOverrideL1 = false }) {
  const isForeign = pr.currency && pr.currency !== "INR";
  const l1Reject = it.decision?.level === "L1" && it.decision?.action === "REJECTED";
  const locked = l1Reject && !canOverrideL1;
  return (
    <tr className="border-t border-slate-100" data-testid={`item-row-${it.icode}`}>
      <td className="px-2 py-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="font-medium text-slate-800 truncate max-w-[160px]" title={it.iname}>
            {it.iname}
          </div>
          {it.jobtype && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[9px] font-bold bg-violet-100 text-violet-800 border border-violet-200 leading-none uppercase tracking-wide"
              data-testid={`item-jobtype-desktop-${it.icode}`}
              title={`Jobwork process — historical comparison filtered by this process`}
            >
              {it.jobtype}
            </span>
          )}
          <ItemInsightPopover item={it} supplierName={pr.supplier} />
        </div>
        <div className="text-[10px] text-slate-500 mono">{it.icode}</div>
        {/* Creator's per-line-item remark (PoRequesti.Remark) */}
        {it.remark && (
          <div
            className="mt-1 text-[10px] text-amber-900 bg-amber-50 border border-amber-200 rounded-sm px-1.5 py-0.5 italic truncate max-w-[200px]"
            data-testid={`item-remark-desktop-${it.icode}`}
            title={it.remark}
          >
            <span className="not-italic font-bold mr-1">Note:</span>{it.remark}
          </div>
        )}
      </td>
      <td className="px-2 py-2 text-right num">{it.qty}</td>
      <td className="px-2 py-2 text-right num">
        {isForeign ? (
          <>
            <span className="text-[10px] text-slate-500 font-normal mr-0.5">{pr.currency}</span>
            {fmtAmt(it.rate)}
          </>
        ) : (
          <>{fmtAmt(it.rate)}</>
        )}
        {it.amt != null && (
          <div
            className="text-[11px] font-semibold text-cyan-800"
            title={isForeign
              ? `qty ${fmtAmt(it.qty)} × ${pr.currency} ${fmtAmt(it.rate)} × ExchRate ${fmtAmt(pr.exch_rate)}`
              : `qty ${fmtAmt(it.qty)} × ₹${fmtAmt(it.rate)}`}
          >
            = ₹{fmtAmt(it.amt)}
          </div>
        )}
        {it.last_rate ? (
          <div className="text-[10px] text-slate-400 flex items-center justify-end gap-1.5 flex-wrap">
            <span>{it.jobtype ? "approved" : "was"} ₹{fmtAmt(it.last_rate)}</span>
            <LastBoughtChip it={it} />
          </div>
        ) : it.jobtype ? (
          <div className="text-[10px] text-slate-400 italic">no history for {it.jobtype}</div>
        ) : null}
        {it.jobtype && it.last_rate && it.price_flag && it.price_flag.score === 0 && (
          <div
            className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 leading-none"
            data-testid={`item-approved-rate-desktop-${it.icode}`}
            title={`Rate matches master-approved ₹${fmtAmt(it.last_rate)} for ${it.jobtype}`}
          >
            ✓ Matches approved
          </div>
        )}
        <div className="mt-1 flex justify-end">
          <Sparkline points={it.price_history || []} />
        </div>
      </td>
      <td className="px-2 py-2">
        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${flagClass(it.price_flag.flag)}`}>
          {priceLabel(it.price_flag)}
        </span>
      </td>
      <td className="px-2 py-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              data-testid={`vendor-flag-${it.icode}`}
              className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border cursor-help ${flagClass(it.vendor_flag.flag)}`}
            >
              {vendorLabel(it.vendor_flag)}
            </span>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-[260px]">
            <VendorTooltipContent vf={it.vendor_flag} />
          </TooltipContent>
        </Tooltip>
        {it.vendor_flag.lowest_vendor && it.vendor_flag.flag !== "green" && (
          <div className="text-[10px] text-slate-500 mt-0.5 flex items-center">
            <ChevronRight size={10} /> {it.vendor_flag.lowest_vendor.slice(0, 24)}
          </div>
        )}
      </td>
      <td className="px-2 py-2">
        {onItemDecide ? (
          <>
            <div className="flex items-center justify-center gap-1">
              <ItemDecideButtons
                it={it}
                onDecide={onItemDecide}
                sizeMode="desktop"
                locked={locked}
                lockedBy={it.decision?.by}
              />
            </div>
            <div className="text-center mt-1">{itemActionPill(it.decision)}</div>
          </>
        ) : (
          <div className="text-center">{itemActionPill(it.decision)}</div>
        )}
      </td>
    </tr>
  );
}

export default function ItemsTable({ pr, acting, onItemDecide, onAutoDecide, onFinalize, readOnly = false, canOverrideL1 = false }) {
  const hasAny = pr.items.some((x) => x.decision?.action);
  // Read-only consumers (e.g. PR Pipeline drawer) pass no-op handlers and we
  // skip rendering any per-item decision buttons / batch action UI.
  const itemDecide = readOnly ? undefined : onItemDecide;
  // Count L1 rejections so we can show a compact banner explaining why some
  // rows are locked. Helps L2 approvers understand the state at a glance.
  const l1RejectCount = pr.items.filter(
    (x) => x.decision?.level === "L1" && x.decision?.action === "REJECTED"
  ).length;
  // Total potential savings if the approver switches every line item to the
  // cheaper alternative surfaced in `vendor_flag.lowest_rate`. Only counts
  // lines where a genuinely cheaper different-vendor bill exists (lowest_rate
  // < rate_inr). All numbers are INR-normalised so foreign-currency PRs sum
  // correctly. Lines with no alt (same-vendor no-alt case, or "New Item") do
  // not contribute — their lowest_rate is null.
  const savingsPerLine = pr.items.map((x) => {
    const vf = x.vendor_flag || {};
    const alt = vf.lowest_rate;
    const cur = x.rate_inr ?? x.rate;
    const qty = Number(x.qty) || 0;
    if (alt == null || cur == null || alt >= cur || qty <= 0) return 0;
    return qty * (cur - alt);
  });
  const totalSavings = savingsPerLine.reduce((s, v) => s + v, 0);
  const savingsLineCount = savingsPerLine.filter((v) => v > 0).length;
  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Line Items ({pr.items.length})</div>
          {totalSavings > 0 && (
            <span
              data-testid="pr-total-savings"
              title={`Approver could save ~₹${fmtAmt(totalSavings)} by switching ${savingsLineCount} line${savingsLineCount > 1 ? "s" : ""} to the cheaper alt vendor shown below`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border border-emerald-300 bg-emerald-50 text-emerald-800"
            >
              <TrendingDown size={11} />
              Potential savings ₹{fmtAmt(totalSavings)}
              <span className="text-emerald-600 font-normal">
                · {savingsLineCount} line{savingsLineCount > 1 ? "s" : ""}
              </span>
            </span>
          )}
        </div>
        {!readOnly && (
          <Button
            size="sm"
            variant="outline"
            onClick={onAutoDecide}
            disabled={acting}
            data-testid="btn-auto-decide"
            className="border-purple-300 text-cyan-800 hover:bg-cyan-50 h-7 text-[11px]"
          >
            <Zap size={12} className="mr-1" />
            Auto-decide green items
          </Button>
        )}
      </div>
      {l1RejectCount > 0 && !canOverrideL1 && (
        <div
          data-testid="l1-lock-banner"
          className="mb-2 flex items-center gap-2 px-2.5 py-1.5 rounded-sm border border-rose-200 bg-rose-50 text-rose-900 text-[11px]"
        >
          <Lock size={12} className="flex-shrink-0" />
          <span>
            <b>{l1RejectCount}</b> line{l1RejectCount > 1 ? "s" : ""} rejected by L1 —
            these are <b>locked</b> and cannot be overturned by L2.
          </span>
        </div>
      )}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-slate-100">
          {pr.items.map((it) => (
            <ItemCardMobile
              key={it.icode + it.iname}
              it={it}
              pr={pr}
              onItemDecide={itemDecide}
              canOverrideL1={canOverrideL1}
            />
          ))}
        </div>
        {/* Desktop table */}
        <table className="hidden md:table w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-2 py-2">Item</th>
              <th className="text-right px-2 py-2">Qty</th>
              <th className="text-right px-2 py-2">Rate</th>
              <th className="text-left px-2 py-2">Price</th>
              <th className="text-left px-2 py-2">Vendor</th>
              {!readOnly && <th className="text-center px-2 py-2 w-[140px]">Decide</th>}
              {readOnly && <th className="text-center px-2 py-2 w-[100px]">Status</th>}
            </tr>
          </thead>
          <tbody>
            {pr.items.map((it) => (
              <ItemRowDesktop
                key={it.icode + it.iname}
                it={it}
                pr={pr}
                onItemDecide={itemDecide}
                canOverrideL1={canOverrideL1}
              />
            ))}
          </tbody>
        </table>
      </div>
      {!readOnly && hasAny && (
        <Button
          onClick={onFinalize}
          disabled={acting}
          data-testid="btn-finalize"
          className="w-full mt-3 bg-cyan-700 hover:bg-cyan-600 text-white"
        >
          <Wand2 size={14} className="mr-1" />
          Finalize PR from per-item decisions
        </Button>
      )}
    </div>
  );
}
